/**
 * Convert text-based block conditions to Formly hideExpression
 * Handles: text, email, password, url, textarea, phone-number blocks
 */

export interface TextConditionProps {
	mode:
		| 'exact'
		| 'not-exact'
		| 'contains'
		| 'not-contains'
		| 'defined'
		| 'undefined'
		| 'equals'
		| 'not-equals'
		| 'regex';
	match?: string;
	ignoreCase?: boolean;
	regex?: string;
	invert?: boolean;
}

/**
 * Convert a text condition to hideExpression
 * Returns expression that evaluates to TRUE when condition is MET
 * (Remember: hideExpression hides when TRUE, so we'll invert later)
 */
export function convertTextCondition(fieldKey: string, props: TextConditionProps): string | undefined {
	const { mode, match, ignoreCase, regex, invert } = props;

	switch (mode) {
		case 'exact':
		case 'equals':
			// Field value must exactly match the specified value
			if (ignoreCase) {
				return `(model['${fieldKey}'] || '').toLowerCase() === ${formatMatchValue(match, true)}`;
			}
			return `model['${fieldKey}'] === ${formatMatchValue(match, false)}`;

		case 'not-exact':
		case 'not-equals':
			// Field value must NOT exactly match the specified value
			if (ignoreCase) {
				return `(model['${fieldKey}'] || '').toLowerCase() !== ${formatMatchValue(match, true)}`;
			}
			return `model['${fieldKey}'] !== ${formatMatchValue(match, false)}`;

		case 'contains':
			// Field value must contain the specified substring
			if (ignoreCase) {
				return `(model['${fieldKey}'] || '').toLowerCase().indexOf(${formatMatchValue(match, true)}) !== -1`;
			}
			return `(model['${fieldKey}'] || '').indexOf(${formatMatchValue(match, false)}) !== -1`;

		case 'not-contains':
			// Field value must NOT contain the specified substring
			if (ignoreCase) {
				return `(model['${fieldKey}'] || '').toLowerCase().indexOf(${formatMatchValue(match, true)}) === -1`;
			}
			return `(model['${fieldKey}'] || '').indexOf(${formatMatchValue(match, false)}) === -1`;

		case 'defined':
			// Field must have a value (not empty string)
			return `!!(model['${fieldKey}'] && model['${fieldKey}'] !== '')`;

		case 'undefined':
			// Field must be empty or undefined
			return `!(model['${fieldKey}'] && model['${fieldKey}'] !== '')`;

		case 'regex': {
			// Field value must match (or not match if inverted) the regex pattern
			if (!regex) {
				return undefined;
			}
			// Validate regex format: must start and end with /
			const literalSignLeft = regex.indexOf('/');
			const literalSignRight = regex.lastIndexOf('/');

			if (literalSignLeft !== 0 || literalSignRight <= literalSignLeft) {
				// Invalid regex format - return undefined to skip this condition
				return undefined;
			}

			// Extract the pattern and flags from the regex literal
			const regexPattern = regex.substring(literalSignLeft + 1, literalSignRight);
			const regexFlags = regex.substring(literalSignRight + 1);

			// Escape special characters in the pattern for safe inclusion in expression
			const escapedPattern = regexPattern.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

			// Build the regex test expression
			if (invert) {
				return `!(new RegExp('${escapedPattern}', '${regexFlags}').test(model['${fieldKey}'] || ''))`;
			}
			return `(new RegExp('${escapedPattern}', '${regexFlags}').test(model['${fieldKey}'] || ''))`;
		}

		default:
			// Unknown mode
			return undefined;
	}
}

/**
 * Format match value for comparison
 * Handles escaping and case conversion
 */
function formatMatchValue(match: string | undefined, lowercase: boolean): string {
	if (match === undefined || match === null) {
		return "''";
	}

	// Escape single quotes in the string
	const escaped = match.replace(/'/g, "\\'");

	if (lowercase) {
		return `'${escaped.toLowerCase()}'`;
	}

	return `'${escaped}'`;
}
